import { Component, Input, ViewEncapsulation, inject, OnInit, ChangeDetectorRef, ChangeDetectionStrategy} from '@angular/core';
import { UserPreferencesService } from '../../../../shared/services/user-preferences/user-preferences.service';
import { MatDialog } from '@angular/material/dialog';
import { Gallery, GalleryRef, ImageItem, ImageSize, ThumbnailsPosition } from 'ng-gallery';
import { Lightbox } from 'ng-gallery/lightbox';
import { TemplateImageEditComponent } from '../template-image-edit/template-image-edit.component';
import { CommonConfirmationDialog } from '../../../../shared/models/user-preferences.model';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { LoaderService } from '../../../../shared/services/loader/loader.service';
import { ToastService } from '../../../../shared/services/toast/toast.service';
import { Constants, UploadOperation } from '../../../../shared/constants/common';
import { GlobalService } from '../../../../shared/services/global/global-service';
import { catchError, concatMap, from, Observable, of, tap } from 'rxjs';
import { MediaFileListComponent } from '../media-file-list/media-file-list.component';
import { FilePreviewDialogComponent } from '../file-preview-dialog/file-preview-dialog.component';
import { HttpClient } from '@angular/common/http';
import { UploadMediaRequest } from '../../../../shared/models/template.model';
import { UserPreferencesData } from '../../../../shared/models/user-preferences.model';

interface MediaFile {
  caption: string;
  data: string;
  url: string;
}

interface ItemInfo {
  formattedItemCode?: string;
  itemCode: string;
  caption?: string;
}

interface ChecklistItem {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- val holds heterogeneous answer shapes (string, string[], MediaFile[]) depending on fieldType
  val: any;
  note?: string;
  media?: MediaFile[];
  action?: ActionInfo[];
}

interface ActionInfo {
  id: number;
  caption: string;
  status: string;
}

@Component({
    selector: 'app-template-media',
    templateUrl: './template-media.component.html',
    styleUrl: './template-media.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone migration is tracked separately (COL-37/COL-36), out of scope here
    standalone: false
})
export class TemplateMediaComponent implements OnInit {
    private userPreferencesService = inject(UserPreferencesService);
    private loaderService = inject(LoaderService);
    private toastService = inject(ToastService);
    private globalService = inject(GlobalService);
    private http = inject(HttpClient);
    private dialog = inject(MatDialog);
    private cdr = inject(ChangeDetectorRef);
    gallery = inject(Gallery);
    lightbox = inject(Lightbox);
 
    userPreferencesData: UserPreferencesData;
    lightboxRef: GalleryRef;
    @Input() checklists: Record<string, ChecklistItem> = {};
    @Input() itemInfo: ItemInfo;
    @Input() sectionNo: number | undefined;
    @Input() recordId: number;
    @Input() moduleName: string;
    @Input() mediaSection: string = Constants.MediaType.Media;
    sectionkey: string;
    loadedImages: Record<string, boolean> = {};
    /** Show the files only: no upload, edit or delete (a read-only inspection, COL-843). */
    @Input() isDisabled = false;

    async ngOnInit(): Promise<void> {
        this.userPreferencesData = this.userPreferencesService.getUserPreferencesData();
        this.sectionkey = [Constants.MediaType.ImageField, Constants.MediaType.MediaAns].includes(this.mediaSection) ? 'val' : 'media';
        this.previewImageConfig();
    }

    getFileUrl(media: MediaFile) {
        let url: string;
        switch((media.caption ?? '').split('.').pop()?.toLowerCase() ?? ''){
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
                url = media.url;
                break;
            case 'xls':
            case 'xlsx':
            case 'csv':
                url = 'assets/icons/excel.svg';
                break;
            case 'pdf':
                url = 'assets/icons/download_pdf.svg';
                break;
            default:
                url = 'assets/icons/fir-icon.svg';
                break;
        }
        return url;
     }

    /** Only actual image files can be shown in the Lightbox preview or opened in the Pintura editor —
     * everything else (PDF/DOCX/etc., relevant now that `MEDIA_ANS` fields accept any file type)
     * renders via an icon in `getFileUrl` and must fall back to a plain download instead. */
    isImageFile(media: MediaFile): boolean {
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(
            (media.caption ?? '').split('.').pop()?.toLowerCase() ?? ''
        );
    }

    onFileTileClick(media: MediaFile, index: number): void {
        if (this.isImageFile(media)) {
            this.previewImages(index);
        } else {
            this.openFilePreview(media);
        }
    }

    /** Opens as a real MatDialog (not an inline `position: fixed` template block) so it gets
     * the same CDK-overlay-managed z-index/stacking as every other dialog in the app. PDFs
     * render directly in an iframe; Office documents (docx/xlsx/pptx) render via Microsoft's
     * public embed viewer (the same technique `DocumentPreviewComponent` uses); anything else
     * shows a "no preview" state with a download button — see `FilePreviewDialogComponent`. */
    openFilePreview(media: MediaFile): void {
        this.dialog.open(FilePreviewDialogComponent, {
            maxWidth: '1200px',
            width: '90%',
            panelClass: 'cmn-dialog',
            data: { media },
        });
    }

    // Check if the image is loaded
    isImageLoaded(media: MediaFile): boolean {
        return !!this.loadedImages[media.url];
    }

    markImageAsLoaded(media: MediaFile): void {
        this.loadedImages[media.url] = true;
    }

    setDefaultImage(event: Event, defaultImagePath = 'assets/images/dummy-checklist-que-image.png'): void {
        (event.target as HTMLImageElement).src = defaultImagePath;
    }

    previewImageConfig() {
        this.lightboxRef = this.gallery.ref('lightbox');

        this.lightboxRef.setConfig({
            imageSize: ImageSize.Contain,
            thumbPosition: ThumbnailsPosition.Bottom,
            thumbs: true,
            counter: true,
            thumbHeight: 80,
            thumbWidth: 70,
        });
    }

    previewImages(index: number) {
        const item = this.checklists[this.itemInfo.formattedItemCode];
        const imageItems = item[this.sectionkey]?.map((item) => new ImageItem({ src: item.url, thumb: item.url }));
        this.lightboxRef.load(imageItems);
        this.lightbox.open(index);
    }

    downloadFile(media: MediaFile): void {
        this.http.get(media.url, { responseType: 'blob' }).subscribe({
            next: (blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = media.data.split('/').pop();
                a.click();
                URL.revokeObjectURL(objectUrl);
            },
            error: () => {
                this.toastService.showError('Error downloading file');
            }
        });
    }

    openPinturaEditImageModal(media: MediaFile): void {
        const dialogRef = this.dialog.open(TemplateImageEditComponent, {
            maxWidth: '1920px',
            width: '90%',
            height: '95%',
            data: { 
                image: media.url, 
                imageKey: media.data, 
                recordId: this.recordId, 
                moduleName: this.moduleName,
                mediaSection: this.mediaSection,
                itemCode: this.itemInfo.formattedItemCode,
                sectionNo: this.sectionNo
            },
            panelClass: 'cmn-dialog',
        });

        dialogRef.afterClosed().subscribe((response) => {
            if (response?.success) {
                media.data = response.mediaKey;
                media.url = response.imageUrl;
            }
        });
    }

    openDeleteImageModal(media: MediaFile, filePicker?: HTMLInputElement) {
        const dialogData: CommonConfirmationDialog = {
            confirmMsg: 'Confirm Delete',
            confirmMsgDescription: 'Are you sure you want to delete this file?',
            buttons: [
                {
                    name: 'No',
                    class: '!text-base',
                    color: 'accent',
                },
                {
                    name: 'Yes, Delete',
                    class: '!text-base',
                    handler: () => {
                        this.deleteFile(media);
                        if (filePicker) {
                            filePicker.value = '';
                        }
                    },
                },
            ],
        };
        this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '500px',
            width: '90%',
            panelClass: 'cmn-dialog',
            data: dialogData,
        });
    }

    deleteFile(media: MediaFile) {
        const request: UploadMediaRequest = {
            recordId: this.recordId,
            moduleName: this.moduleName,
            referenceId: '0',
            referenceTable: [Constants.MediaType.ImageField, Constants.MediaType.MediaAns].includes(this.mediaSection)
                            ? Constants.MediaType.ImageField
                            : Constants.MediaType.Media,
            itemCode: this.itemInfo.itemCode,
            sectionNo: this.sectionNo,
            file: undefined,
            mediaKey: media.data,
            action: UploadOperation.Delete,
            companyId: this.userPreferencesData.selectedCompanyId,
            uploadBy: this.userPreferencesData.userId,
            uploadedByName: this.userPreferencesData.userName
        };

        this.loaderService.showLoader("Deleting file...");
        this.globalService.uploadMedia(request)
            .pipe(
                catchError(() => {
                    this.toastService.showError('Failed to delete file.');
                    this.loaderService.hideLoader();
                    return of(null);
                })
            )
            .subscribe((response) => {
                if (response) {
                    const item = this.checklists[this.itemInfo.formattedItemCode];
                    const idx = item[this.sectionkey].indexOf(media);
                    item[this.sectionkey].splice(idx, 1);
                    this.cdr.detectChanges();
                    this.toastService.showSuccess('File deleted successfully.');
                }
                this.loaderService.hideLoader();
            });
    }

    onDragOver(event: Event) {
        event.preventDefault();
        event.stopPropagation();
    }

    onDrop(event: DragEvent, filePicker: HTMLInputElement) {
        this.onDragOver(event);

        const files = event?.dataTransfer?.files;
        if (!files || files.length === 0) return;

        const dataTransfer = new DataTransfer();
        for (let i = 0; i < files?.length; i++) {
            dataTransfer.items.add(files[i]);
        }

        filePicker.files = dataTransfer.files;
        this.selectFiles(event, filePicker);
    }

    selectFiles(event: Event, filePicker: HTMLInputElement) {
        let selectedFiles: FileList | null;
        if (filePicker.files && filePicker.files.length > 0) {
            selectedFiles = filePicker.files;
        } else {
            const input = event.target as HTMLInputElement;
            selectedFiles = input.files;
        }

        if (selectedFiles && selectedFiles.length > 0) {
            const oversizedFiles = Array.from(selectedFiles).filter(
                (file) => file.size > Constants.MaxMediaFileSizeBytes
            );
            if (oversizedFiles.length > 0) {
                this.toastService.showError(
                    `File(s) exceed the ${Constants.MaxMediaFileSizeBytes / (1024 * 1024)}MB size limit: ${oversizedFiles.map(f => f.name).join(', ')}`
                );
                filePicker.value = '';
                return;
            }

            // MediaAns (the new `FieldType.Media` field) accepts any file type — no whitelist.
            if (this.mediaSection !== Constants.MediaType.MediaAns) {
                const validFileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/png'];

                if(this.mediaSection == Constants.MediaType.Media){
                    validFileTypes.push('image/jpeg', 'image/jpg', 'image/png', 'image/png',
                        'application/pdf', '.txt', '.csvapplication/vnd.ms-excel', 'application/msword',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
                }

                const invalidFiles = Array.from(selectedFiles).filter(
                    (file) => !validFileTypes.includes(file.type)
                );

                if (invalidFiles.length > 0) {
                    this.toastService.showError(this.mediaSection == Constants.MediaType.ImageField
                        ? 'Only image files (JPEG, JPG, PNG) are allowed.'
                        : 'Only files allowed are JPED, JPD, PNG, Excel, Word, & PDF');
                    filePicker.value = ''; // Reset the file input
                    return;
                }
            }

            this.loaderService.showLoader('Uploading File(s), please wait...');
            let successfulUploads = 0;
            let failedUploads = 0;
            const totalFiles = selectedFiles.length;

            from(Array.from(selectedFiles))
                .pipe(
                    concatMap((file, index) => {
                        // Update progress message
                        this.loaderService.showLoader(`Uploading file ${index + 1} of ${totalFiles}, please wait...`);

                        return this.uploadFile(file).pipe(
                            tap({
                                next: () => {
                                    successfulUploads++;
                                    filePicker.value = '';
                                },
                                error: () => failedUploads++
                            }),
                            catchError((err) => {
                                console.error(`Error uploading file ${index + 1}:`, err);
                                return of(null); // Continue with next file
                            })
                        );
                    })
                )
                .subscribe({
                    complete: () => {
                        if (successfulUploads > 0) {
                            const word = successfulUploads === 1 ? 'file' : 'files';
                            this.toastService.showSuccess(`${successfulUploads} ${word} uploaded successfully.`);
                        }

                        if (failedUploads > 0) {
                            const word = failedUploads === 1 ? 'file' : 'files';
                            this.toastService.showError(`${failedUploads} ${word} failed to upload.`);
                        }
                        this.cdr.detectChanges();
                        this.loaderService.hideLoader();
                    }
                });
        }
    }

    uploadFile(file: File): Observable<{ mediaKey: string; imageUrl?: string; success?: boolean }> {
        const request: UploadMediaRequest = {
            recordId: this.recordId,
            moduleName: this.moduleName,
            referenceId: '0',
            referenceTable: [Constants.MediaType.ImageField, Constants.MediaType.MediaAns].includes(this.mediaSection)
                ? Constants.MediaType.ImageField
                : Constants.MediaType.Media,
            itemCode: this.itemInfo.itemCode,
            sectionNo: this.sectionNo,
            file: file,
            mediaKey: '',
            action: UploadOperation.Upload,
            companyId: this.userPreferencesData.selectedCompanyId,
            uploadBy: this.userPreferencesData.userId,
            uploadedByName: this.userPreferencesData.userName
        };

        return this.globalService.uploadMedia(request).pipe(
            tap((response) => {
                const mediaInfo: MediaFile = {
                    caption: file.name,
                    data: response.mediaKey,
                    url: response.imageUrl ?? ''
                };

                const item = this.checklists[this.itemInfo.formattedItemCode];

                if (!item[this.sectionkey]) {
                    item[this.sectionkey] = [];
                }
                item[this.sectionkey].push(mediaInfo);
            }),
            catchError((err) => {
                console.error('Error while uploading file:', err);
                throw err;
            })
        );
    }

    openImageModal(): void {
        this.dialog.open(MediaFileListComponent, {
            maxWidth: '1920px',
            width: '90%',
            height: '95%',
            data: { itemInfo: this.itemInfo, 
                media: this.checklists[this.itemInfo.formattedItemCode]?.[this.sectionkey], 
                recordId: this.recordId,
                moduleName: this.moduleName,
                mediaSection: this.mediaSection,
                isDisabled: this.isDisabled,
                
            },
            panelClass: 'cmn-dialog',
        });
    }
}